// MaturaConeHeightClosedTask.js
// Zadanie maturalne – zamknięte (1 pkt)
// Stożek: z długości tworzącej lub promienia i kąta rozwarcia liczymy wysokość.

import React, { useContext } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import ABCDInput from '../../../../components/inputs/ABCDInput';
import TriangleDiagram from '../../../../components/diagrams/TriangleDiagram';
import { ThemeContext } from '../../../../context/ThemeContext';

// ================== UTILSY ==================

const rndInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// pomocniczo: ładne LaTeX-y postaci k * sqrt(n)
function timesSqrtLatex(k, n) {
  // jeżeli jest praktycznie całkowite, zaokrąglamy
  if (Math.abs(k - Math.round(k)) < 1e-9) {
    const c = Math.round(k);
    if (n === 1) return `${c}`;
    if (c === 1) return `\\sqrt{${n}}`;
    return `${c}\\sqrt{${n}}`;
  }

  // spróbuj zapisać k jako "ładny" ułamek
  const frac = toNiceFraction(k);
  if (frac) {
    const { num, den } = frac;

    // forma mieszana, jeśli > 1 (np. 10/3 = 3 1/3)
    if (num > den) {
      const whole = Math.floor(num / den);
      const remainder = num - whole * den;
      if (remainder === 0) {
        if (n === 1) return `${whole}`;
        if (whole === 1) return `\\sqrt{${n}}`;
        return `${whole}\\sqrt{${n}}`;
      }
      const mixed = `${whole}\\dfrac{${remainder}}{${den}}`;
      if (n === 1) return mixed;
      return `${mixed}\\sqrt{${n}}`;
    }

    // zwykły ułamek właściwy
    if (n === 1) return `\\dfrac{${num}}{${den}}`;
    return `\\dfrac{${num}}{${den}}\\sqrt{${n}}`;
  }

  // w ostateczności – zostawiamy jako dziesiętny (rzadko)
  const rounded = Math.round(k * 1000) / 1000;
  if (n === 1) return `${rounded}`;
  if (rounded === 1) return `\\sqrt{${n}}`;
  return `${rounded}\\sqrt{${n}}`;
}

function toNiceFraction(x) {
  const tol = 1e-9;
  const denominators = [2, 3, 4, 5, 6, 8, 10, 12];

  for (const den of denominators) {
    const num = Math.round(x * den);
    if (Math.abs(num / den - x) < tol && num !== 0) {
      const d = gcd(Math.abs(num), den);
      return { num: num / d, den: den / d };
    }
  }
  return null;
}

function gcd(a, b) {
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a;
}

// ================== LOKALNY RYSUNEK: PRZEKRÓJ OSIOWY (z odbiciem) ==================
// Wersja „1:1 jak TriangleDiagram”: ta sama skala, padding i pozycjonowanie kąta.
export function ConeAxialHintDiagram({ rVal, hVal, alphaLabel }) {
  const { theme } = useContext(ThemeContext);
  const textColor = theme === 'light' ? '#000' : '#fff';
  const fillColor = theme === 'light' ? '#f9f9f9' : '#000';

  // dokładnie jak w TriangleDiagram: sideA = a (poziomy), sideB = b (pionowy), sideC = przeciwprostokątna
  const sideA = typeof rVal === 'number' ? rVal : Number(rVal);
  const sideB = typeof hVal === 'number' ? hVal : Number(hVal);

  const a = Number.isFinite(sideA) && sideA > 0 ? sideA : 3;
  const b = Number.isFinite(sideB) && sideB > 0 ? sideB : 4;
  const c = Math.sqrt(a * a + b * b);

  // ✅ 1:1 jak w TriangleDiagram
  const maxSide = Math.max(a, b, c);
  const scale = 160 / maxSide;
  const padding = 50;

  const width = 2 * a * scale + 2 * padding;   // ✅ pełna średnica (2r)
  const height = b * scale + 2 * padding;

  // ✅ A (środek podstawy) ustawiamy na środku szerokości
  const ax = padding + a * scale;
  const ay = height - padding;

  const bx = ax + a * scale;
  const by = ay;

  const cx = ax;
  const cy = ay - b * scale;

  // lewa krawędź podstawy (już się mieści)
  const dx = ax - a * scale;
  const dy = ay;

  // pozycjonowanie etykiety kąta: 1:1 jak TriangleDiagram dla angleAt="C"
  const angleDeg = (Math.atan(a / b) * 180) / Math.PI;
  let offsetY;
  if (angleDeg < 20) offsetY = +70;
  else if (angleDeg < 30) offsetY = +45;
  else if (angleDeg < 40) offsetY = +25;
  else if (angleDeg < 50) offsetY = +25;
  else if (angleDeg < 60) offsetY = +10;
  else if (angleDeg < 70) offsetY = +7;
  else offsetY = +5;

  // helper
  const mid = (x1, y1, x2, y2) => ({ x: (x1 + x2) / 2, y: (y1 + y2) / 2 });
  const abMid = mid(ax, ay, bx, by);
  const acMid = mid(ax, ay, cx, cy);
  const bcMid = mid(bx, by, cx, cy);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', margin: '0 auto', maxWidth: '100%', height: 'auto' }}>
      {/* prawa połówka (pełna) */}
      <polygon
        points={`${ax},${ay} ${bx},${by} ${cx},${cy}`}
        fill={fillColor}
        stroke={textColor}
        strokeWidth="2"
      />

      {/* lewa połówka (przerywana) */}
      <polyline
        points={`${dx},${dy} ${cx},${cy} ${ax},${ay}`}
        fill="none"
        stroke={textColor}
        strokeWidth="2"
        strokeDasharray="6 6"
        opacity="0.9"
      />

      {/* podstawa pełnego przekroju (średnica) */}
      <line x1={dx} y1={dy} x2={bx} y2={by} stroke={textColor} strokeWidth="1.6" opacity="0.9" />

      {/* oś (wysokość) */}
      <line x1={ax} y1={ay} x2={cx} y2={cy} stroke={textColor} strokeWidth="2" />

      {/* kąt prosty w A (tak jak TriangleDiagram) */}
      <path
        d={`M ${ax + 15} ${ay} A 15 15 0 0 0 ${ax} ${ay - 15}`}
        fill="none"
        stroke={textColor}
        strokeWidth="2"
      />
      <circle cx={ax + 6} cy={ay - 6} r="2" fill={textColor} />

      {/* wierzchołki A,B,C (pozycje 1:1 jak TriangleDiagram) */}
      <foreignObject x={ax - 25} y={ay + 5} width="20" height="20">
        <div style={{ color: textColor, fontSize: '14px' }}>
          <InlineMath math={'A'} />
        </div>
      </foreignObject>

      <foreignObject x={bx + 5} y={by + 5} width="20" height="20">
        <div style={{ color: textColor, fontSize: '14px' }}>
          <InlineMath math={'B'} />
        </div>
      </foreignObject>

      <foreignObject x={cx - 25} y={cy - 20} width="20" height="20">
        <div style={{ color: textColor, fontSize: '14px' }}>
          <InlineMath math={'C'} />
        </div>
      </foreignObject>

      {/* podpisy boków: r, h, l — rozmieszczenie identyczne jak w TriangleDiagram */}
      <foreignObject x={abMid.x - 25} y={ay + 8} width="50" height="24">
        <div style={{ color: textColor, textAlign: 'center' }}>
          <InlineMath math={'r'} />
        </div>
      </foreignObject>

      <foreignObject x={ax - 38} y={acMid.y - 12} width="32" height="24">
        <div style={{ color: textColor, textAlign: 'right' }}>
          <InlineMath math={'h'} />
        </div>
      </foreignObject>

      <foreignObject x={(bx + cx) / 2 + 14} y={(by + cy) / 2 - 24} width="60" height="24">
        <div style={{ color: textColor, textAlign: 'left' }}>
          <InlineMath math={'l'} />
        </div>
      </foreignObject>

      {/* ✅ kąt: pozycja i rozmiar 1:1 jak TriangleDiagram (angleAt="C") */}
      <foreignObject x={cx + 5} y={cy + offsetY} width="42" height="24">
        <div style={{ color: textColor, fontSize: '14px' }}>
          <InlineMath math={alphaLabel} />
        </div>
      </foreignObject>
    </svg>
  );
}

// ================== GENERATOR ==================

// tworzę jedną spójną pulę: wariant "tworząca" i wariant "promień"
function generateJune2025Original() {
  return {
    type: 'MaturaConeHeightClosedTask',
    data: {
      givenType: 'slant',
      angleDeg: 60,
      halfAngle: 30,
      l: 6,
      r: 3,
      options: [
        { label: 'A', latex: '3', isCorrect: false },
        { label: 'B', latex: '2\\sqrt{3}', isCorrect: false },
        { label: 'C', latex: '3\\sqrt{3}', isCorrect: true },
        { label: 'D', latex: '3\\sqrt{5}', isCorrect: false },
      ],
      correctIndex: 2,
      hLatex: '3\\sqrt{3}',
      hValue: 3 * Math.sqrt(3),
    },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_CZE',
      taskNumber: 26,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === 'original' &&
    generationOptions.session === '2025_CZE'
  ) {
    return generateJune2025Original();
  }

  const angleOptions = [60, 90, 120]; // kąt rozwarcia stożka
  const angleDeg = angleOptions[rndInt(0, angleOptions.length - 1)];
  const halfAngle = angleDeg / 2; // kąt w trójkącie prostokątnym

  // losujemy, co jest dane: tworząca czy promień
  const givenTypes = ['slant', 'radius'];
  const givenType = givenTypes[rndInt(0, givenTypes.length - 1)];

  let l = null; // tworząca
  let r = null; // promień
  let hLatex = '';
  let hValue = 0;

  if (givenType === 'slant') {
    // bierzemy parzyste tworzące, żeby ładnie wychodziły pierwiastki
    const slantCandidates = [4, 6, 8, 10];
    l = slantCandidates[rndInt(0, slantCandidates.length - 1)];

    if (angleDeg === 60) {
      // halfAngle = 30°, h = l cos 30° = l * √3 / 2
      const coef = l / 2;
      hLatex = timesSqrtLatex(coef, 3);
    } else if (angleDeg === 90) {
      // halfAngle = 45°, h = l cos 45° = l * √2 / 2
      const coef = l / 2;
      hLatex = timesSqrtLatex(coef, 2);
    } else {
      // angleDeg === 120, halfAngle = 60°, h = l cos 60° = l / 2
      const coef = l / 2;
      hLatex = `${coef}`;
    }

    hValue = l * Math.cos((halfAngle * Math.PI) / 180);
    r = l * Math.sin((halfAngle * Math.PI) / 180);
  } else {
    // givenType === 'radius'
    if (angleDeg === 60) {
      // halfAngle = 30°, h = r / tan 30° = r √3
      const rCandidates = [2, 3, 4];
      r = rCandidates[rndInt(0, rCandidates.length - 1)];
      hLatex = timesSqrtLatex(r, 3);
      hValue = r * Math.sqrt(3);
    } else if (angleDeg === 90) {
      // halfAngle = 45°, h = r / tan 45° = r
      const rCandidates = [2, 3, 4, 5];
      r = rCandidates[rndInt(0, rCandidates.length - 1)];
      hLatex = `${r}`;
      hValue = r;
    } else {
      // angleDeg === 120, halfAngle = 60°, h = r / tan 60° = r / √3 = (r√3)/3
      const rCandidates = [3, 6, 9];
      r = rCandidates[rndInt(0, rCandidates.length - 1)];
      const coef = r / 3;
      hLatex = timesSqrtLatex(coef, 3);
      hValue = r / Math.sqrt(3);
    }

    l = Math.sqrt(r * r + hValue * hValue);
  }

  const correctLatex = hLatex;

  // ───── DYSTRYKTORY ─────
  const distractorsPool = [];

  // (1) wzięta druga przyprostokątna zamiast wysokości (np. promień)
  const rCandidate =
    givenType === 'slant'
      ? (() => {
          if (angleDeg === 60) return `${l / 2}`; // r = l sin 30 = l/2
          if (angleDeg === 90) return timesSqrtLatex(l / 2, 2); // r = l sin 45
          return timesSqrtLatex(l / 2, 3); // r = l sin 60
        })()
      : `${r}`;

  if (rCandidate !== correctLatex) {
    distractorsPool.push({ latex: rCandidate });
  }

  // (2) błąd z tangensem / cosinusem
  let tanMistakeLatex = null;
  if (givenType === 'slant') {
    if (angleDeg === 60) {
      tanMistakeLatex = timesSqrtLatex(l / 3, 3); // h = l tan 30
    } else if (angleDeg === 90) {
      tanMistakeLatex = `${l}`; // h = l tan 45
    } else {
      tanMistakeLatex = timesSqrtLatex(l, 3); // h = l tan 60
    }
  } else {
    // dane r, ale ktoś użyje cos zamiast tan: h = r cos(halfAngle)
    if (angleDeg === 60) {
      tanMistakeLatex = timesSqrtLatex(r / 2, 3);
    } else if (angleDeg === 90) {
      tanMistakeLatex = timesSqrtLatex(r / 2, 2);
    } else {
      tanMistakeLatex = String(r / 2).replace(".", "{,}");
    }
  }

  if (
    tanMistakeLatex &&
    tanMistakeLatex !== correctLatex &&
    !distractorsPool.find((d) => d.latex === tanMistakeLatex)
  ) {
    distractorsPool.push({ latex: tanMistakeLatex });
  }

  // (3) liczba całkowita "obok" poprawnej (zaokrąglenie wysokości)
  const rounded = Math.round(hValue);
  const roundedLatex = `${rounded}`;
  if (
    roundedLatex !== correctLatex &&
    !distractorsPool.find((d) => d.latex === roundedLatex)
  ) {
    distractorsPool.push({ latex: roundedLatex });
  }

  // (4) trochę większa liczba całkowita
  const biggerLatex = `${rounded + 1}`;
  if (
    biggerLatex !== correctLatex &&
    !distractorsPool.find((d) => d.latex === biggerLatex)
  ) {
    distractorsPool.push({ latex: biggerLatex });
  }

  // ───── ZAPEWNIAMY MINIMUM 3 DYSTRYKTORY ─────
  const fallbackLatex = ['1', '2', '3', '\\sqrt{2}', '\\sqrt{3}', '\\sqrt{5}'];
  for (const latex of fallbackLatex) {
    if (distractorsPool.length >= 3) break;
    if (latex === correctLatex) continue;
    if (!distractorsPool.find((d) => d.latex === latex)) {
      distractorsPool.push({ latex });
    }
  }

  const pickedDistractors = shuffle(distractorsPool).slice(0, 3);

  const rawOptions = [
    { latex: correctLatex, isCorrect: true },
    ...pickedDistractors.map((d) => ({ ...d, isCorrect: false })),
  ];

  const shuffled = shuffle(rawOptions).map((opt, idx) => ({
    ...opt,
    label: String.fromCharCode(65 + idx),
  }));

  const correctIndex = shuffled.findIndex((opt) => opt.isCorrect);

  return {
    type: 'MaturaConeHeightClosedTask',
    data: {
      givenType,
      angleDeg,
      halfAngle,
      l,
      r,
      options: shuffled,
      correctIndex,
      hLatex: correctLatex,
      hValue,
    },
  };
}

// ================== TREŚĆ ZADANIA ==================

export function renderTask({ data }) {
  const { givenType, angleDeg, l, r, options } = data;

  const angleLatex = `${angleDeg}^{\\circ}`;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – zamknięte (0–1 pkt).</strong>
      </p>

      {givenType === 'slant' ? (
        <p>
          Tworząca stożka ma długość <InlineMath math={String(l)} />. Kąt
          rozwarcia tego stożka ma miarę <InlineMath math={angleLatex} />.
        </p>
      ) : (
        <p>
          Promień podstawy stożka ma długość <InlineMath math={String(r)} />.
          Kąt rozwarcia tego stożka ma miarę <InlineMath math={angleLatex} />.
        </p>
      )}

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
        <br />
        Wysokość tego stożka jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ================== INPUT (ABCD) ==================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === 'number'
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ================== CHECK ==================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== 'number') {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ================== POPRAWNA ODPOWIEDŹ ==================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ================== ODPOWIEDŹ UŻYTKOWNIKA ==================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== 'number' ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ================== WYJAŚNIENIE ==================

function cosHalfExactLatex(angleDeg) {
  if (angleDeg === 60) return '\\dfrac{\\sqrt{3}}{2}'; // cos 30°
  if (angleDeg === 90) return '\\dfrac{\\sqrt{2}}{2}'; // cos 45°
  if (angleDeg === 120) return '\\dfrac{1}{2}'; // cos 60°
  return `\\cos\\,\\dfrac{${angleDeg}^{\\circ}}{2}`;
}

function tanHalfExactLatex(angleDeg) {
  if (angleDeg === 60) return '\\dfrac{1}{\\sqrt{3}}'; // tan 30°
  if (angleDeg === 90) return '1'; // tan 45°
  if (angleDeg === 120) return '\\sqrt{3}'; // tan 60°
  return `\\tan\\,\\dfrac{${angleDeg}^{\\circ}}{2}`;
}

export function renderExplanation({ data }) {
  const { givenType, angleDeg, halfAngle, l, r, hLatex } = data;

  const angleLatex = `${angleDeg}^{\\circ}`;
  const halfLatex = `${halfAngle}^{\\circ}`;
  const cosHalf = cosHalfExactLatex(angleDeg);
  const tanHalf = tanHalfExactLatex(angleDeg);

  // wartości numeryczne tylko do kształtu rysunku
  const hVal =
    givenType === 'slant'
      ? l * Math.cos((halfAngle * Math.PI) / 180)
      : r / Math.tan((halfAngle * Math.PI) / 180);
  const rVal =
    givenType === 'slant'
      ? l * Math.sin((halfAngle * Math.PI) / 180)
      : r;
  const lVal = Math.sqrt(hVal * hVal + rVal * rVal);

  return (
    <div className="space-y-4" style={{ textAlign: 'left' }}>
      {/* KROK 1 */}
      <p>
        <strong>Krok 1. Przekrój osiowy stożka.</strong>
      </p>

      <p>
        Rozpatrujemy przekrój osiowy stożka. Otrzymujemy trójkąt równoramienny,
        w którym ramionami są tworzące stożka. Wysokość <InlineMath math={'h'} />{' '}
        dzieli ten przekrój na dwa przystające trójkąty prostokątne, więc w jednym
        z nich występuje kąt <InlineMath math={halfLatex} /> (czyli połowa kąta
        rozwarcia <InlineMath math={`\\alpha=${angleLatex}`} />).
      </p>

      {/* ✅ rysunek dynamiczny + przerywane odbicie (pełny przekrój osiowy) */}
      <div style={{ textAlign: 'center' }}>
        <ConeAxialHintDiagram rVal={rVal} hVal={hVal} alphaLabel={halfLatex} />
      </div>

      {/* KROK 2 */}
      <p>
        <strong>
          Krok 2. Zapis zależności trygonometrycznej i obliczenie wysokości.
        </strong>
      </p>

      {givenType === 'slant' ? (
        <>
          <p>
            Długość tworzącej jest równa <InlineMath math={String(l)} />, więc w
            naszym trójkącie przeciwprostokątna ma długość <InlineMath math={String(l)} />
            , a wysokość stożka oznaczamy przez <InlineMath math={'h'} />.
          </p>

          {/* rysunek z podstawioną tworzącą */}
          <div style={{ textAlign: 'center' }}>
            <TriangleDiagram
              a={rVal}
              b={hVal}
              c={lVal}
              angleAt="C"
              labelA={'r'}
              labelB={'h'}
              labelC={String(l)}
              alphaLabel={halfLatex}
              showSideLabels={true}
            />
          </div>

          <p>
            Kąt między wysokością a tworzącą ma miarę <InlineMath math={halfLatex} />.
            W tym trójkącie przeciwprostokątną jest <InlineMath math={'l'} />, a przyprostokątną
            odpowiadającą wysokości jest <InlineMath math={'h'} />. Korzystamy z cosinusa:
          </p>

          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\cos\\,\\frac{\\alpha}{2} = \\dfrac{h}{l}'} />
            <BlockMath math={`\\cos ${halfLatex} = \\dfrac{h}{${l}}`} />
            <BlockMath math={`${cosHalf} = \\dfrac{h}{${l}}`} />
          </div>

          <p>Po przekształceniu:</p>

          <div style={{ textAlign: 'center' }}>
            <BlockMath math={`h = ${l}\\cdot ${cosHalf} = ${hLatex}.`} />
          </div>
        </>
      ) : (
        <>
          <p>
            Znamy promień podstawy stożka: <InlineMath math={String(r)} />.
            W trójkącie prostokątnym przy kącie <InlineMath math={halfLatex} /> leży
            wysokość <InlineMath math={'h'} />, a bok naprzeciw tego kąta ma długość{' '}
            <InlineMath math={'r'} />.
          </p>

          {/* rysunek z podstawionym promieniem */}
          <div style={{ textAlign: 'center' }}>
            <TriangleDiagram
              a={rVal}
              b={hVal}
              c={lVal}
              angleAt="C"
              labelA={String(r)}
              labelB={'h'}
              labelC={'l'}
              alphaLabel={halfLatex}
              showSideLabels={true}
            />
          </div>

          <p>Korzystamy z tangensa:</p>

          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\tan\\,\\frac{\\alpha}{2} = \\dfrac{r}{h}'} />
            <BlockMath math={`\\tan ${halfLatex} = \\dfrac{${r}}{h}`} />
            <BlockMath math={`${tanHalf} = \\dfrac{${r}}{h}`} />
          </div>

          <p>Po przekształceniu otrzymujemy:</p>

          <div style={{ textAlign: 'center' }}>
            <BlockMath math={`h = \\dfrac{${r}}{${tanHalf}} = ${hLatex}.`} />
          </div>
        </>
      )}

      <p>
        Zatem wysokość tego stożka jest równa <InlineMath math={hLatex} />.
      </p>
    </div>
  );
}
